/**
 * ContentStudioVersionsPage -- Version Lifecycle Management
 * Route: /content-studio/versions
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Shows version KPI cards, a DataTable with row actions, and a
 * static Version Lifecycle info card.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageSpinner } from "../../components/PageSpinner";
import { useAuthStore } from "../../stores/authStore";
import { useVersionStore } from "../../stores/versionStore";
import { useVersions } from "../../hooks/useVersions";
import { VersionCreateForm } from "../../components/VersionCreateForm";
import { apiClient } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { DataTable } from "../../components/DataTable";
import type { Column, RowAction } from "../../components/DataTable";
import {
  Pencil,
  CheckCircle,
  Archive,
  Eye,
  Copy,
  Trash2,
  RotateCcw,
  Upload,
  Info,
  GitBranch,
  Plus,
} from "lucide-react";
import type { Version } from "../../types";
import { formatLocalDateTime } from "../../lib/dateFormat";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATE_BADGE_CLASSES: Record<string, string> = {
  Draft: "bg-amber-100 text-amber-800 border-amber-300",
  Active: "bg-green-50 text-green-700 border-green-300",
  Archived: "bg-gray-100 text-gray-500 border-gray-300",
};

const STATE_ICONS: Record<string, React.ReactNode> = {
  Draft: <Pencil size={14} className="text-amber-600" aria-hidden="true" />,
  Active: <CheckCircle size={14} className="text-green-600" aria-hidden="true" />,
  Archived: <Archive size={14} className="text-gray-400" aria-hidden="true" />,
};

const STATE_LINE_COLORS: Record<string, string> = {
  Draft: "bg-amber-400",
  Active: "bg-green-500",
  Archived: "bg-gray-300",
};

function getInitials(name?: string): string {
  if (!name) return "??";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ContentStudioVersionsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const triggerRefresh = useVersionStore((s) => s.triggerRefresh);
  const setSelectedVersion = useVersionStore((s) => s.setSelectedVersion);
  const { data: versions, isLoading, error, refetch } = useVersions();

  const [createOpen, setCreateOpen] = useState(false);

  // Creator display names arrive inline on each Version (`created_by_name`)
  // from /api/versions, so no separate /api/users lookup is needed. That
  // endpoint is Super-Admin-only and would 403-then-logout a Content Admin.

  // RBAC
  const canEdit = user?.roles?.some((r) => ["Content Admin", "Super Admin"].includes(r)) ?? false;
  const canPublish =
    user?.roles?.some((r) => ["Content Admin", "Super Admin"].includes(r)) ?? false;

  const allVersions = versions ?? [];
  const totalCount = allVersions.length;
  const activeVersion = allVersions.find((v) => v.state === "Active");
  const draftVersion = allVersions.find((v) => v.state === "Draft");
  const archivedCount = allVersions.filter((v) => v.state === "Archived").length;

  // -- Actions
  const handlePublish = async (row: Version) => {
    try {
      await apiClient.post(`/api/versions/${row.id}/publish`, {});
      refetch();
      triggerRefresh();
      showToast({ variant: "success", title: "Version published", description: `"${row.version_name}" is now active.` });
    } catch {
      showToast({ variant: "error", title: "Publish failed", description: "Could not publish the version. Please try again." });
    }
  };

  const handleArchive = async (row: Version) => {
    try {
      await apiClient.patch(`/api/versions/${row.id}`, { state: "Archived" });
      refetch();
      triggerRefresh();
      showToast({ variant: "success", title: "Version archived", description: `"${row.version_name}" has been archived.` });
    } catch {
      showToast({ variant: "error", title: "Archive failed", description: "Could not archive the version. Please try again." });
    }
  };

  const handleCloneDraft = async (row: Version) => {
    try {
      await apiClient.post(`/api/versions/${row.id}/clone`, {});
      refetch();
      triggerRefresh();
      showToast({ variant: "success", title: "Draft created", description: `A new draft has been cloned from "${row.version_name}".` });
    } catch {
      showToast({ variant: "error", title: "Clone failed", description: "Could not clone the version. Please try again." });
    }
  };

  const handleRestore = async (row: Version) => {
    try {
      await apiClient.patch(`/api/versions/${row.id}`, { state: "Active" });
      refetch();
      triggerRefresh();
      showToast({ variant: "success", title: "Version restored", description: `"${row.version_name}" has been restored as active.` });
    } catch {
      showToast({ variant: "error", title: "Restore failed", description: "Could not restore the version. Please try again." });
    }
  };

  const handleDelete = async (row: Version) => {
    try {
      await apiClient.delete(`/api/versions/${row.id}`);
      refetch();
      triggerRefresh();
      showToast({ variant: "success", title: "Version deleted", description: `"${row.version_name}" has been permanently deleted.` });
    } catch {
      showToast({ variant: "error", title: "Delete failed", description: "Could not delete the version. Please try again." });
    }
  };

  const handleCreateSubmit = async (data: { version_name: string; changelog_notes: string; from_scratch: boolean }) => {
    try {
      const created = await apiClient.post<Version>("/api/versions", {
        version_name: data.version_name,
        changelog_notes: data.changelog_notes,
        state: "Draft",
        from_scratch: data.from_scratch,
      });
      // Switch the global version selector to the newly-created draft so
      // every Content Studio page (Buckets / Activities / Competencies /
      // Scoring / Triads / Questions) shows the right context immediately.
      // Without this, the user has to manually pick the new version from
      // the banner dropdown — and the auto-select fallback in
      // VersionSelector picks the *first* draft, which usually isn't the
      // one they just created.
      if (created?.id) {
        setSelectedVersion(created);
      }
      setCreateOpen(false);
      refetch();
      triggerRefresh();
      const description = data.from_scratch
        ? `"${data.version_name}" has been created as an empty draft. Import buckets, activities, and competencies to populate it.`
        : `"${data.version_name}" has been created as a draft.`;
      showToast({ variant: "success", title: "Version created", description });
    } catch {
      showToast({ variant: "error", title: "Creation failed", description: "Could not create the version. Please try again." });
    }
  };

  // -- Columns
  const columns: Column<Version>[] = [
    {
      key: "version_name",
      header: "Version",
      sortable: true,
      render: (_val, row) => (
        <div className="flex items-center gap-2">
          {STATE_ICONS[row.state]}
          <span className={`text-sm font-medium`}>{row.version_name}</span>
        </div>
      ),
    },
    {
      key: "state",
      header: "State",
      sortable: true,
      render: (_val, row) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            STATE_BADGE_CLASSES[row.state] ?? "bg-gray-100 text-gray-500"
          }`}
        >
          {row.state}
        </span>
      ),
    },
    {
      key: "created_by",
      header: "Created By",
      sortable: true,
      render: (_val, row) => {
        const createdById = row.created_by;
        if (!createdById) return <span className="text-brand-steel/50">—</span>;
        // Prefer the server-resolved display name; fall back to the raw id
        // only when the lookup couldn't resolve a user (e.g. deleted record).
        const displayName = row.created_by_name?.trim() || createdById;
        return (
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-500 text-white text-[10px] font-semibold shrink-0">
              {getInitials(displayName)}
            </span>
            <span className="text-sm text-gray-900">{displayName}</span>
          </div>
        );
      },
    },
    {
      key: "created_at",
      header: "Created",
      sortable: true,
      render: (_val, row) => <span className="text-sm text-gray-700">{formatLocalDateTime(row.created_at)}</span>,
    },
  ];

  // -- Row actions
  const actions: RowAction<Version>[] = [
    {
      label: "View",
      icon: <Eye size={14} />,
      onClick: (row) => navigate(`/content-studio/versions/${row.id}`),
    },
    {
      label: "Publish",
      icon: <Upload size={14} />,
      onClick: handlePublish,
      hidden: (row) => row.state !== "Draft" || !canPublish,
      requireConfirm: true,
      confirmTitle: "Publish version?",
      confirmDescription: "This version will become active. Ensure it is ready for use before publishing.",
      confirmLabel: "Publish",
    },
    {
      label: "Clone as Draft",
      icon: <Copy size={14} />,
      onClick: handleCloneDraft,
      hidden: (row) => row.state !== "Active" || !canEdit,
      requireConfirm: true,
      confirmTitle: "Create Draft from Active?",
      confirmDescription:
        "A new draft will be created from the current active version. All version-scoped content will be deep-cloned.",
      confirmLabel: "Create Draft",
    },
    {
      label: "Archive",
      icon: <Archive size={14} />,
      onClick: handleArchive,
      hidden: (row) => (row.state !== "Draft" && row.state !== "Active") || !canEdit,
      requireConfirm: true,
      confirmTitle: "Archive version?",
      confirmDescription: "This version will be moved to the archived state. You can restore it later.",
      confirmLabel: "Archive",
    },
    {
      label: "Restore as Active",
      icon: <RotateCcw size={14} />,
      onClick: handleRestore,
      hidden: (row) => row.state !== "Archived" || !canEdit,
      requireConfirm: true,
      confirmTitle: "Restore version?",
      confirmDescription: "This will restore the archived version as active.",
      confirmLabel: "Restore",
    },
    {
      label: "Delete",
      icon: <Trash2 size={14} />,
      onClick: handleDelete,
      variant: "destructive",
      hidden: (row) => row.state !== "Draft" || !canEdit,
      requireConfirm: true,
      confirmTitle: "Delete version?",
      confirmDescription:
        "This action cannot be undone. The draft version and all its content will be permanently deleted.",
      confirmLabel: "Delete",
    },
  ];

  if (isLoading) return <PageSpinner label="Loading versions" />;

  return (
    <main className="p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-gray-900">Versions</h1>
        {canEdit && (
          <Button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2">
            <Plus size={16} aria-hidden="true" /> Create Version
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Total Versions",
            value: totalCount,
            sub: "All versions across states",
            iconBg: "bg-brand-navy/10",
            icon: <GitBranch size={18} className="text-brand-navy" aria-hidden="true" />,
            valueColor: "text-gray-900",
          },
          {
            label: "Active",
            value: activeVersion ? 1 : 0,
            sub: "Live in assessments",
            iconBg: "bg-green-100",
            icon: <CheckCircle size={18} className="text-green-600" aria-hidden="true" />,
            valueColor: "text-green-600",
          },
          {
            label: "Draft",
            value: draftVersion ? 1 : 0,
            sub: "Editable, not live",
            iconBg: "bg-amber-100",
            icon: <Pencil size={18} className="text-amber-600" aria-hidden="true" />,
            valueColor: "text-amber-600",
          },
          {
            label: "Archived",
            value: archivedCount,
            sub: "Read-only, can be restored",
            iconBg: "bg-gray-200",
            icon: <Archive size={18} className="text-gray-400" aria-hidden="true" />,
            valueColor: "text-gray-500",
          },
        ].map((card) => (
          <Card key={card.label} className="border-brand-steel/50 bg-white shadow-sm">
            <CardContent className="px-5 py-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-brand-navy/70 uppercase tracking-wider">{card.label}</p>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${card.iconBg}`}>
                  {card.icon}
                </div>
              </div>
              <p className={`text-2xl font-bold ${card.valueColor}`}>{card.value}</p>
              <p className="text-xs text-brand-steel mt-1">{card.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Versions Table */}
      {error ? (
        <div role="alert" className="p-4 bg-red-50 border border-brand-error/40 rounded-lg text-sm text-brand-error">
          {error.message}
        </div>
      ) : (
        <div className="mb-6">
          <DataTable<Version>
            data={allVersions}
            columns={columns}
            keyField="id"
            searchable
            actions={actions}
            emptyMessage="No versions found. Create your first version to get started."
            onRowClick={(row) => navigate(`/content-studio/versions/${row.id}`)}
          />
        </div>
      )}

      {/* Version Lifecycle */}
      <Card className="border-brand-steel/50 bg-white shadow-sm">
        <CardContent className="p-5 py-0">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Version Lifecycle</h3>
          <div className="flex items-center gap-3 mb-4">
            {/* Draft */}
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100">
                <Pencil size={16} className="text-amber-600" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Draft</p>
                <p className="text-xs text-brand-steel">Editable, not live</p>
              </div>
            </div>
            <span className="text-brand-steel/40 text-lg" aria-hidden="true">
              &rarr;
            </span>
            {/* Active */}
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100">
                <CheckCircle size={16} className="text-green-600" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Active</p>
                <p className="text-xs text-brand-steel">Immutable, live in assessments</p>
              </div>
            </div>
            <span className="text-brand-steel/40 text-lg" aria-hidden="true">
              &rarr;
            </span>
            {/* Archived */}
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100">
                <Archive size={16} className="text-gray-400" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Archived</p>
                <p className="text-xs text-brand-steel">Read-only, can be restored</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Version Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-lg shadow-2xl max-w-md border-brand-steel/50">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-gray-900">Create Version</DialogTitle>
            <DialogDescription className="text-sm text-brand-steel">Create a new draft version.</DialogDescription>
          </DialogHeader>
          <VersionCreateForm onSubmit={handleCreateSubmit} onCancel={() => setCreateOpen(false)} />
        </DialogContent>
      </Dialog>
    </main>
  );
}

export default ContentStudioVersionsPage;
